
  import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
  import { AjaxHelper, getToken, ICurrentUser, IPaginatedList } from '@samc/common';
  import { Tab, Tabs, Btn, Grid, VMenu, IEditDeleteButtonsRendererOnClickParams, IGridComponent, CellDoubleClickedEvent } from '@samc/centricity-core';
  import { IEntityData, ITaskGetRequest } from '@samc/taskmanagement-ui';
  import { IAutosaveNotificationResponse, INotificationTabGridParams, Keys } from './Interfaces';
  import { GetAutoSaveRedirectUrl, GetStepOverrides } from './Utils';
  import { AutosaveNotificationDataSource } from './DataSources';
  import { ITaskResponse } from '@/Interfaces';
  import { UserDataSource } from '@/Sources';
  import { IPackageEntity } from '@/utils';

  type TmTaskGridWithPreviewRefresh = HTMLTmTaskGridElement & {
    refreshPreviewData(): Promise<void>;
  };

  @Component({
    name: 'notification-badge',
    components: { Tab, Tabs, Btn, Grid, VMenu }
  })
  export default class NotificationBadge extends Vue {
    @Prop({ type: Boolean, default: false })
    public enableTaskManagement!: boolean;

    @Prop({ type: String, default: '' })
    public taskManagementUrl!: string;

    @Prop({ type: Number, default: 15 })
    public approvalNotificationMaxRows!: number;

    public showMenu = false;
    public notificationCount: number = 0;
    public tabIndex = 0;
    public approvalUserSource = new UserDataSource();
    public loadedEntities: IEntityData[] = [];
    public jwtToken: string | null = null;
    public currentTabs: Record<string, INotificationTabGridParams> = {
      'Approvals': {
        dataSource: undefined,
        tabTitle: 'Approvals',
        rowCount: 0,
        rowCountBadgeClasses: 'bg-[#f9ae40] text-white',
        initialize: false,
        disabled: false
      },
      'EMails': {
        dataSource: undefined,
        tabTitle: 'EMails',
        rowCount: 0,
        rowCountBadgeClasses: 'bg-message-green text-white',
        initialize: false,
        disabled: false
      },
      'Autosaves': {
        dataSource: new AutosaveNotificationDataSource(15, true),
        tabTitle: 'Autosaves',
        rowCount: 0,
        rowCountBadgeClasses: 'bg-[#FF6F00] text-white',
        initialize: false,
        disabled: false
      }
    };

    public get tabNames(): string[] {
      if (this.enableTaskManagement === true && this.taskManagementUrl !== '') {
        return ['Approvals', 'EMails', 'Autosaves'];
      }
      return ['Autosaves'];
    }

    public get activeTabName(): string {
      return this.tabNames[this.tabIndex] ?? 'Autosaves';
    }

    public get currentUser(): ICurrentUser | null {
      return this.$user ?? null;
    }

    public get canEditTasks(): boolean {
      const user = this.currentUser;
      if (user === null) {
        return false;
      }
      return user.hasEntitlement?.('Edit/Close Tasks') === true;
    }

    public get hideTaskEditColumn(): boolean {
      return this.canEditTasks !== true;
    }

    public get showTaskNotificationGrids(): boolean {
      return this.getTaskNotificationUserEmail() !== null;
    }

    public get approvalsNotificationFilters(): Partial<ITaskGetRequest> {
      const userEmail = this.getTaskNotificationUserEmail();
      if (userEmail === null) {
        return {};
      }

      return {
        assignedTo: userEmail,
        approvalRequired: true,
      };
    }

    public get emailsNotificationFilters(): Partial<ITaskGetRequest> {
      const userEmail = this.getTaskNotificationUserEmail();
      if (userEmail === null) {
        return {};
      }

      return {
        userEmail,
        sendEmail: true,
        approvalRequired: false,
      } as Partial<ITaskGetRequest>;
    }

    private verificationToken: string = '';

    public async mounted() {
      this.verificationToken = await this.$verificationToken();
      this.jwtToken = getToken();
      await this.loadEntities();
      await this.LoadCount();
      document.body.addEventListener('keyup', this.handleKey);
    }

    public async unmounted() {
      document.body.removeEventListener('keyup', this.handleKey);
    }

    private getTaskNotificationUserEmail(): string | null {
      if (this.enableTaskManagement !== true) {
        return null;
      }

      if (this.taskManagementUrl.length === 0) {
        return null;
      }

      const user = this.currentUser;
      if (user === null) {
        return null;
      }

      const email = user.email;
      if (email === null || email === undefined || email.length === 0) {
        return null;
      }

      return email;
    }

    public async loadEntities(): Promise<void> {
      const helper = new AjaxHelper('/datasource/packagesbasic', '', '', null);
      const result = await helper.getData<IPaginatedList<IPackageEntity>>();
      this.loadedEntities = result.items;
    }

    private getTaskNotificationGridRef(refName: 'approvalsTaskGrid' | 'emailsTaskGrid'): TmTaskGridWithPreviewRefresh | undefined {
      const refValue = this.$refs[refName];
      if (refValue === undefined || refValue === null) {
        return undefined;
      }

      if (Array.isArray(refValue)) {
        const first = refValue[0];
        if (first === undefined || first === null) {
          return undefined;
        }
        return first as TmTaskGridWithPreviewRefresh;
      }

      return refValue as TmTaskGridWithPreviewRefresh;
    }

    public wireApprovalsTaskGrid(): void {
      const grid = this.getTaskNotificationGridRef('approvalsTaskGrid');
      const user = this.currentUser;
      const userEmail = this.getTaskNotificationUserEmail();
      if (grid === undefined || user === null || userEmail === null) {
        return;
      }

      grid.apiRoot = this.taskManagementUrl;
      grid.jwt = this.jwtToken ?? undefined;
      grid.token = this.verificationToken;
      grid.currentUser = user;
      grid.filters = this.approvalsNotificationFilters;
      grid.userSource = this.approvalUserSource;
      grid.entitySource = this.loadedEntities;
    }

    public wireEmailsTaskGrid(): void {
      const grid = this.getTaskNotificationGridRef('emailsTaskGrid');
      const user = this.currentUser;
      const userEmail = this.getTaskNotificationUserEmail();
      if (grid === undefined || user === null || userEmail === null) {
        return;
      }

      grid.apiRoot = this.taskManagementUrl;
      grid.jwt = this.jwtToken ?? undefined;
      grid.token = this.verificationToken;
      grid.currentUser = user;
      grid.filters = this.emailsNotificationFilters;
      grid.userSource = this.approvalUserSource;
      grid.entitySource = this.loadedEntities;
    }

    private wireActiveTaskNotificationGrid(): void {
      if (this.activeTabName === 'Approvals') {
        this.wireApprovalsTaskGrid();
        return;
      }

      if (this.activeTabName === 'EMails') {
        this.wireEmailsTaskGrid();
      }
    }

    private async refreshActiveTaskNotificationGrid(): Promise<void> {
      if (this.activeTabName === 'Approvals') {
        await this.refreshApprovalsTaskGrid();
        return;
      }

      if (this.activeTabName === 'EMails') {
        await this.refreshEmailsTaskGrid();
      }
    }

    private async loadActiveTaskNotificationTab(): Promise<void> {
      if (this.activeTabName !== 'Approvals' && this.activeTabName !== 'EMails') {
        return;
      }

      await this.$nextTick();
      await this.$nextTick();
      this.wireActiveTaskNotificationGrid();
      await this.refreshActiveTaskNotificationGrid();
    }

    public async refreshApprovalsTaskGrid(): Promise<void> {
      const grid = this.getTaskNotificationGridRef('approvalsTaskGrid');
      if (grid === undefined) {
        return;
      }

      await grid.refreshPreviewData();
    }

    public async refreshEmailsTaskGrid(): Promise<void> {
      const grid = this.getTaskNotificationGridRef('emailsTaskGrid');
      if (grid === undefined) {
        return;
      }

      await grid.refreshPreviewData();
    }

    @Watch('$user')
    public onUserChanged(): void {
      if (this.showMenu !== true) {
        return;
      }

      void this.loadActiveTaskNotificationTab();
    }

    public isTabDisabled(tabName: string): boolean {
      const tab = this.currentTabs[tabName];
      return tab !== null && tab !== undefined ? tab.disabled : false;
    }

    public getTabBadgeCount(index: number): number {
      const tabName = this.tabNames[index];
      if (tabName === undefined) {
        return 0;
      }

      const tab = this.currentTabs[tabName];
      if (tab === undefined) {
        return 0;
      }

      return tab.rowCount;
    }

    public getTabBadgeClasses(index: number): string {
      const tabName = this.tabNames[index];
      if (tabName === undefined) {
        return '';
      }

      const tab = this.currentTabs[tabName];
      if (tab === undefined) {
        return '';
      }

      return tab.rowCountBadgeClasses;
    }

    public handleKey(e: KeyboardEvent) {
      if (e.key === Keys.Escape) {
        this.showMenu = false;
      }
    }

    public async LoadCount() {
      let totalCount = 0;
      const autosaveHelper = new AjaxHelper('/configApi/v1/autosavenotifications', this.verificationToken, '', null);
      const autosaveCount = await autosaveHelper.getData<number>();
      const autosaveTab = this.currentTabs['Autosaves'];
      if (autosaveTab !== undefined) {
        this.$set(autosaveTab, 'rowCount', autosaveCount);
      }
      totalCount = totalCount + autosaveCount;

      if (this.enableTaskManagement === true && this.taskManagementUrl !== '') {
        const taskHelper = new AjaxHelper(this.taskManagementUrl, this.verificationToken, null, getToken());
        const details = await taskHelper.getData<ITaskResponse<{ approvalTasksCount: number; emailTasksCount?: number }>>('/Task/DetailsCount');
        if (details.success === true) {
          const approvalCount = details.data.approvalTasksCount;
          const emailCount = details.data.emailTasksCount ?? 0;
          const approvalsTab = this.currentTabs['Approvals'];
          if (approvalsTab !== undefined) {
            this.$set(approvalsTab, 'rowCount', approvalCount);
          }
          const emailsTab = this.currentTabs['EMails'];
          if (emailsTab !== undefined) {
            this.$set(emailsTab, 'rowCount', emailCount);
          }
          totalCount = totalCount + approvalCount + emailCount;
        }
      }

      this.notificationCount = totalCount;
    }

    public customStepValueGetter = function(params: { data: { importProcessId: number; step: string } }) {
      const importProcessId = params.data.importProcessId;
      return GetStepOverrides(params.data.step, importProcessId);
    }

    @Watch('tabIndex')
    public onTabIndexChanged(): void {
      void this.loadActiveTaskNotificationTab();
    }

    @Watch('showMenu')
    public async onShowMenuChange(newShowMenu: boolean) {
      if (newShowMenu === true) {
        this.tabIndex = 0;
        await this.loadActiveTaskNotificationTab();
        await this.LoadCount();
      }
    }

    public gotoResults() {
      if (this.activeTabName === 'Approvals') {
        window.location.href = '/TaskManagement/ApprovalNotifications';
        return;
      }
      if (this.activeTabName === 'EMails') {
        window.location.href = '/TaskManagement/EmailNotifications';
        return;
      }
      if (this.activeTabName === 'Autosaves') {
        window.location.href = '/Autosave/NotificationResults';
      }
    }

    public onRowDoubleClick(e: CellDoubleClickedEvent) {
      if (this.activeTabName === 'Autosaves') {
        const autosaveNotification = { ...e.data } as IAutosaveNotificationResponse;
        window.location.href = GetAutoSaveRedirectUrl(autosaveNotification);
      }
    }

    public async onApprovalTaskSaved() {
      await this.refreshApprovalsTaskGrid();
      await this.LoadCount();
    }

    public async onEmailTaskSaved() {
      await this.refreshEmailsTaskGrid();
      await this.LoadCount();
    }

    public async clearAll() {
      this.$loading('Removing all autosaves');
      try {
        const helper = new AjaxHelper('/configApi/v1/autosavenotifications', this.verificationToken, null, null);

        const response = await helper.delete<boolean>('all', {});
        if (!response) {
          this.$alert.error(`An error occurred Removing all autosaves.`);
          return;
        }
        this.$emit('update', false);
        const currentGrid = (this.$refs.autosaveGrid as any) as Array<IGridComponent>;
        currentGrid[0].refreshDatasource();
        $alert.success(`All autosaves Removed.`, `Successfully removed all autosaves.`, true);
      }
      catch (err: any) {
        this.$alert.error(`An error occurred Removing all autosaves.`);
      }
      finally {
        this.$loading();
      }

      await this.LoadCount();
      if (this.notificationCount === 0) {
        this.showMenu = false;
      }
    }

    public async onRemoveClick(e: IEditDeleteButtonsRendererOnClickParams<IAutosaveNotificationResponse>) {
      this.$loading('Removing autosave');
      try {
        const helper = new AjaxHelper('/configApi/v1/autosavenotifications', this.verificationToken, null, null);

        const response = await helper.delete<boolean>('', { ids: [e.data.id] });
        if (!response) {
          this.$alert.error(`An error occurred removing autosave.`);
          return;
        }
        const currentGrid = (this.$refs.autosaveGrid as any) as Array<IGridComponent>;
        currentGrid[0].refreshDatasource();
        $alert.success(`Autosave removed.`, `Successfully removed autosave.`);
      }
      catch (err: any) {
        this.$alert.error(`An error occurred removing autosave.`);
      }
      finally {
        this.$loading();
      }

      await this.LoadCount();
    }

    public async onContinueClick(e: IEditDeleteButtonsRendererOnClickParams<IAutosaveNotificationResponse>) {
      window.location.href = GetAutoSaveRedirectUrl(e.data);
    }
  }
